import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Heart, Sparkle, Flower } from "@phosphor-icons/react";
import { FadeUp, SectionLabel, Stat } from "@/components/Reveal";
import {
  LOGO, FOUNDER, GROUP, UPCOMING, NONPROFIT_2,
  KIDS_CLASS, FLOWER_GROUP, FRENCHIE_GROUP, FAMILY_YOGA,
  GALENTINES_GROUP, ZOOMBA_FLOW,
  HERO_BG,
  TESTIMONIALS, PARTNERS, CONTACT
} from "@/data/content";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover object-[70%_center]" />
          <div className="absolute inset-0 bg-gradient-to-b from-cream/30 via-cream/5 to-cream" />
          <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/70 to-transparent md:from-cream/95 md:via-cream/40 md:to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-32 lg:pb-44">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4 }}>
            <SectionLabel className="mb-10">Vol. I · A wellness community</SectionLabel>
          </motion.div>
          <motion.h1
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[14vw] sm:text-[10vw] lg:text-[8.4vw] leading-[0.92] tracking-tight max-w-6xl text-balance"
          >
            Girlhood,<br />
            <span className="font-serif-italic text-teal">Community</span><br />
            &amp; Wellness.
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 1.1, delay: 0.35 }}
            className="font-light text-lg md:text-xl text-charcoal/75 max-w-xl mt-10 leading-relaxed"
          >
            Flowtastics — where <span className="text-teal">movement meets community.</span> Yoga, mindfulness, and art woven into every flow.
          </motion.p>
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 1, delay: 0.55 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a href="https://www.momoyoga.com/flowtastics/teacher-profile" target="_blank" rel="noreferrer" data-testid="hero-cta-book" className="group rounded-full bg-charcoal text-cream px-7 py-4 text-[11px] tracking-editorial uppercase inline-flex items-center gap-3 hover:bg-teal transition-all duration-500">
              Book a Class <ArrowUpRight size={16} weight="light" className="group-hover:rotate-45 transition-transform duration-500" />
            </a>
            <Link to="/about" data-testid="hero-cta-about" className="rounded-full border border-charcoal/30 px-7 py-4 text-[11px] tracking-editorial uppercase hover:border-teal hover:text-teal transition-all duration-500">
              Our Story
            </Link>
          </motion.div>
        </div>

        {/* Floating logo lotus */}
        <motion.img
          src={LOGO} alt="" aria-hidden
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 0.85, scale: 1 }} transition={{ duration: 2 }}
          className="hidden lg:block absolute right-10 bottom-12 w-44 h-44 object-contain mix-blend-multiply animate-float"
        />
      </section>

      {/* MARQUEE TAG LINE */}
      <section className="bg-cream py-10 border-y border-charcoal/10 overflow-hidden">
        <div className="flex gap-16 whitespace-nowrap animate-[shimmer_30s_linear_infinite]">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-16">
              {["Movement With Meaning", "More Than Yoga", "Flow With Purpose", "Movement Meets Community", "Art • Wellness • Empowerment"].map((t, i) => (
                <span key={i} className="font-serif italic text-3xl md:text-5xl text-charcoal/40">
                  {t} <span className="text-gold mx-6">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-40 flex justify-center" data-testid="manifesto">
        <FadeUp>
          <p className="font-serif italic text-3xl md:text-4xl text-teal text-center leading-snug">Flowtastics — where movement meets community.</p>
        </FadeUp>
      </section>

      {/* EDITORIAL IMAGE COLLAGE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-28">
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <FadeUp className="col-span-7 row-span-2"><div className="aspect-[4/5] rounded-3xl overflow-hidden"><img src={FAMILY_YOGA} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s]" /></div></FadeUp>
          <FadeUp delay={0.1} className="col-span-5"><div className="aspect-[5/4] rounded-3xl overflow-hidden"><img src={KIDS_CLASS} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s]" /></div></FadeUp>
          <FadeUp delay={0.2} className="col-span-5"><div className="aspect-square rounded-3xl overflow-hidden bg-blush/40 grain p-10 flex flex-col justify-end">
            <Flower size={36} weight="light" className="text-teal mb-4" />
            <p className="font-serif italic text-2xl md:text-3xl leading-tight text-charcoal">"Leave each class feeling stronger, more present, and filled with gratitude."</p>
            <p className="text-[10px] uppercase tracking-editorial text-charcoal/60 mt-4">— Sophia Vito</p>
          </div></FadeUp>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="bg-blush/30 py-28 md:py-36" data-testid="home-events">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <SectionLabel className="mb-6">Recent Flows</SectionLabel>
              <h2 className="font-serif text-5xl md:text-7xl tracking-tight leading-[1.02]">Where we've<br />flowed.</h2>
            </div>
            <a href="https://www.momoyoga.com/flowtastics/teacher-profile" target="_blank" rel="noreferrer" className="rounded-full bg-charcoal text-cream px-6 py-3 text-[11px] uppercase tracking-editorial hover:bg-teal transition-all inline-flex items-center gap-2" data-testid="home-book-momoyoga">
              Book on Momoyoga <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-10">
            <FadeUp className="bg-cream rounded-3xl overflow-hidden group">
              <div className="aspect-[16/10] overflow-hidden"><img src={GALENTINES_GROUP} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s]" /></div>
              <div className="p-8">
                <div className="text-[10px] uppercase tracking-editorial text-teal mb-3">Galentines · February</div>
                <h3 className="font-serif text-3xl mb-3">Galentines Flow</h3>
                <p className="text-charcoal/70 font-light leading-relaxed">Hosted at Zest Collective.</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.08} className="bg-cream rounded-3xl overflow-hidden group">
              <div className="aspect-[16/10] overflow-hidden"><img src={ZOOMBA_FLOW} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s]" /></div>
              <div className="p-8">
                <div className="text-[10px] uppercase tracking-editorial text-teal mb-3">New Year · Zumba Flow</div>
                <h3 className="font-serif text-3xl mb-3">Zumba Flow</h3>
                <p className="text-charcoal/70 font-light leading-relaxed">Hosted at ETFashion.Co.</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.16} className="bg-cream rounded-3xl overflow-hidden group">
              <div className="aspect-[16/10] overflow-hidden"><img src={FLOWER_GROUP} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s]" /></div>
              <div className="p-8">
                <div className="text-[10px] uppercase tracking-editorial text-teal mb-3">Floral Flow · Community</div>
                <h3 className="font-serif text-3xl mb-3">Bouquets & Breath</h3>
                <p className="text-charcoal/70 font-light leading-relaxed">Hosted at Circus Complex Co.</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.24} className="bg-cream rounded-3xl overflow-hidden group">
              <div className="aspect-[16/10] overflow-hidden"><img src={FRENCHIE_GROUP} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s]" /></div>
              <div className="p-8">
                <div className="text-[10px] uppercase tracking-editorial text-teal mb-3">Frenchie Flow · Charity</div>
                <h3 className="font-serif text-3xl mb-3">Frenchie Flow</h3>
                <p className="text-charcoal/70 font-light leading-relaxed">Yoga + Frenchie cuddles.</p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* FOUNDER SPOTLIGHT */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-40 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid="home-founder">
        <FadeUp className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-blush/40">
            <img src={FOUNDER} alt="Sophia Vito, Founder of Flowtastics" className="w-full h-full object-cover" />
          </div>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-7">
          <SectionLabel className="mb-6">Founder</SectionLabel>
          <h2 className="font-serif text-6xl md:text-7xl tracking-tight leading-[0.95]">
            Sophia <span className="font-serif-italic text-teal">Vito.</span>
          </h2>
          <p className="font-serif italic text-2xl text-charcoal/75 mt-6 max-w-xl">Counselor. Yoga instructor. Builder of girlhood.</p>
          <div className="space-y-5 mt-8 text-charcoal/80 font-light text-lg leading-relaxed max-w-2xl">
            <p>Sophia has spent the past year deepening her work in counseling while continuing to grow as a yoga instructor — teaching for five years and recently expanding her practice to include strength training.</p>
            <p>Inspired by both the counseling and creative worlds, she created Flowtastics: a community that blends mindful yoga flows with the healing principles of counseling, and partners with women-owned businesses and mission-driven organizations.</p>
          </div>
          <Link to="/about" className="inline-flex items-center gap-2 mt-10 text-[11px] uppercase tracking-editorial group" data-testid="home-read-story">
            Read the full story <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </FadeUp>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-40" data-testid="home-testimonials">
        <FadeUp>
          <SectionLabel className="mb-6">From the Community</SectionLabel>
          <h2 className="font-serif text-5xl md:text-7xl tracking-tight max-w-3xl leading-[1.02]">
            Words from <span className="font-serif-italic text-teal">our women.</span>
          </h2>
        </FadeUp>
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 mt-16">
          {TESTIMONIALS.slice(0, 4).map((t, i) => (
            <FadeUp key={i} delay={i * 0.08} className="border-t border-charcoal/15 pt-8">
              <Sparkle size={20} weight="light" className="text-gold mb-5" />
              <p className="font-serif italic text-2xl md:text-3xl leading-snug text-charcoal/90">"{t.quote}"</p>
              <div className="mt-6 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-editorial">{t.name}</div>
                <div className="text-[10px] uppercase tracking-editorial text-charcoal/50">{t.detail}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* PARTNERS PREVIEW */}
      <section className="bg-cream border-y border-charcoal/10 py-16 overflow-hidden" data-testid="home-partners-strip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <p className="text-[11px] uppercase tracking-editorial text-charcoal/60">In partnership with women-owned</p>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4 md:gap-x-14 font-serif italic text-xl md:text-2xl text-charcoal/65">
            {PARTNERS.map((p) => <span key={p.name}>{p.name}</span>)}
          </div>
        </div>
      </section>

      {/* IMPACT CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-36 grid lg:grid-cols-2 gap-10 items-center" data-testid="home-impact-cta">
        <FadeUp>
          <div className="rounded-3xl overflow-hidden bg-blush/30 border-2 border-blush">
            <img src={NONPROFIT_2} alt="" className="w-full h-auto block p-2" />
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <SectionLabel className="mb-6">Flows With Purpose</SectionLabel>
          <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02]">
            Every breath<br />gives <span className="font-serif-italic text-teal">back.</span>
          </h2>
          <p className="text-charcoal/75 font-light text-lg leading-relaxed mt-8 max-w-xl">
            Aerial Silk profits support Circus Complex Co. Frenchie Flow profits support the Animal Rescue League of Boston. September flows give to the Jimmy Fund. Movement is our love letter to the world.
          </p>
          <Link to="/partners" className="inline-flex items-center gap-2 mt-10 text-[11px] uppercase tracking-editorial group" data-testid="home-impact-link">
            See our impact <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </FadeUp>
      </section>

      {/* NEWSLETTER */}
      <Newsletter />
    </div>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, { email });
      toast.success("Welcome to the flow", { description: "You're on the list. See you on the mat." });
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally { setLoading(false); }
  };

  return (
    <section className="bg-blush/40 grain py-28 md:py-36" data-testid="home-newsletter">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <SectionLabel className="mb-6 justify-center inline-flex">The Letter</SectionLabel>
        <h2 className="font-serif text-5xl md:text-7xl tracking-tight leading-[1.02]">
          Slow news. <br /><span className="font-serif-italic text-teal">Soft Sundays.</span>
        </h2>
        <p className="mt-6 text-charcoal/70 font-light text-lg max-w-xl mx-auto">
          Receive event invitations, founder notes, and our monthly cause — straight to your inbox. No noise. Only intention.
        </p>
        <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" data-testid="newsletter-form">
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
            className="flex-1 rounded-full bg-cream/80 border border-charcoal/15 px-6 py-4 text-sm focus:outline-none focus:border-teal"
            data-testid="newsletter-email-input"
          />
          <button type="submit" disabled={loading}
            className="rounded-full bg-charcoal text-cream px-7 py-4 text-[11px] tracking-editorial uppercase hover:bg-teal transition-all disabled:opacity-60"
            data-testid="newsletter-submit-button">
            {loading ? "Joining…" : "Subscribe"}
          </button>
        </form>
        <p className="text-[10px] uppercase tracking-editorial text-charcoal/45 mt-6">No spam. Unsubscribe anytime.</p>
      </div>
    </section>
  );
}
